const RECOVERY_KEY = 'kis-rx:chunk-recovery'
const RECOVERY_WINDOW_MS = 2 * 60 * 1000
const STABLE_CLEAR_DELAY_MS = 10 * 1000
const MAX_RECOVERY_ATTEMPTS = 1
const CACHE_BUST_PARAM = '__app_reload'

function errorText(error) {
  const parts = [
    error?.message,
    error?.stack,
    error?.payload?.message,
    error?.payload?.stack,
    error?.reason?.message,
    error?.reason?.stack,
    error?.target?.src,
    error?.target?.href,
  ]
  try {
    parts.push(String(error))
  } catch {
    // Ignore non-stringifiable browser event objects.
  }
  return parts.filter(Boolean).join(' ')
}

function recoveryRouteKey() {
  if (typeof window === 'undefined') return ''
  const url = new URL(window.location.href)
  url.searchParams.delete(CACHE_BUST_PARAM)
  return `${url.pathname}${url.search}${url.hash}`
}

function removeCacheBustParam() {
  try {
    const url = new URL(window.location.href)
    if (!url.searchParams.has(CACHE_BUST_PARAM)) return
    url.searchParams.delete(CACHE_BUST_PARAM)
    window.history?.replaceState?.(window.history.state, '', url.toString())
  } catch {
    // URL cleanup is cosmetic; recovery can continue without it.
  }
}

function recoveryMarkerForRoute(routeKey) {
  try {
    const marker = JSON.parse(window.sessionStorage.getItem(RECOVERY_KEY) || '{}')
    if (marker.routeKey !== routeKey || Date.now() - Number(marker.at || 0) >= RECOVERY_WINDOW_MS) {
      return null
    }
    return marker
  } catch {
    return null
  }
}

function markRecovery(routeKey, source, error, attempts) {
  try {
    window.sessionStorage.setItem(RECOVERY_KEY, JSON.stringify({
      routeKey,
      source,
      at: Date.now(),
      attempts,
      error: errorText(error).slice(0, 500),
    }))
  } catch {
    // Storage can fail in private browsing; recovery should still proceed.
  }
}

function clearRecoveryMarkerForRoute(routeKey) {
  try {
    const marker = JSON.parse(window.sessionStorage.getItem(RECOVERY_KEY) || '{}')
    if (!marker.routeKey || marker.routeKey === routeKey) {
      window.sessionStorage.removeItem(RECOVERY_KEY)
    }
  } catch {
    try {
      window.sessionStorage.removeItem(RECOVERY_KEY)
    } catch {
      // Storage can fail in private browsing; there is nothing else to clear.
    }
  }
}

export function isChunkLoadError(error) {
  const text = errorText(error)
  if (!text) return false
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading chunk \d+ failed|ChunkLoadError|Unable to preload CSS/i.test(text)
}

export function recoverFromChunkLoadError(error, { source = 'chunk-load' } = {}) {
  if (typeof window === 'undefined' || !isChunkLoadError(error)) return false
  const routeKey = recoveryRouteKey()
  const marker = recoveryMarkerForRoute(routeKey)
  const attempts = Number(marker?.attempts || 0)
  if (attempts >= MAX_RECOVERY_ATTEMPTS) return false

  const nextAttempt = attempts + 1
  markRecovery(routeKey, source, error, nextAttempt)
  const url = new URL(window.location.href)
  url.searchParams.set(CACHE_BUST_PARAM, `${Date.now().toString(36)}-${nextAttempt}`)
  window.location.replace(url.toString())
  return true
}

export function installChunkLoadRecovery() {
  if (typeof window === 'undefined' || window.__kisRxChunkRecoveryInstalled) return
  window.__kisRxChunkRecoveryInstalled = true

  // A shell that stays healthy should not consume a future update recovery attempt.
  // Keep the marker briefly so a genuinely stale shell can retry without looping forever.
  setTimeout(() => {
    clearRecoveryMarkerForRoute(recoveryRouteKey())
    removeCacheBustParam()
  }, STABLE_CLEAR_DELAY_MS)

  window.addEventListener('vite:preloadError', event => {
    event.preventDefault()
    recoverFromChunkLoadError(event, { source: 'vite-preload' })
  })

  window.addEventListener('unhandledrejection', event => {
    if (recoverFromChunkLoadError(event.reason || event, { source: 'unhandled-rejection' })) {
      event.preventDefault()
    }
  })

  window.addEventListener('error', event => {
    recoverFromChunkLoadError(event.error || event, { source: 'window-error' })
  }, true)
}
